import type { LatLong } from '@iheartjane/geolocation';
import type { Nullable } from '@iheartjane/types';

import type { FulfillmentMode } from '@jane/bloom/types/schemas/bloom/fulfillmentMode';
import type { PremiumReservationMode } from '@jane/bloom/types/schemas/marketplace/reservationMode';
import type { BloomMenuStore } from '@jane/bloom/types/schemas/roots/bloom/menu';
import { nearbySort } from '@jane/bloom/utils/geo/nearbySort';
import { getStoresWithDetails } from '@jane/bloom/viewModels/getStoresWithDetails';

interface StoreCriteria {
  currentPinnedStore: Nullable<BloomMenuStore> | undefined;
  deliverableStoreIds?: Nullable<string[]>;
  fulfillmentMode: FulfillmentMode;
  reservationMode: PremiumReservationMode;
  searchAddressCoords: Partial<LatLong>;
}

interface FilteredStores {
  availableStores: BloomMenuStore[];
  currentStore?: BloomMenuStore;
  deliveryStores: BloomMenuStore[];
  medStores: BloomMenuStore[];
  pickupStores: BloomMenuStore[];
  recStores: BloomMenuStore[];
}

export const filteredAndSortedStores = (
  stores: BloomMenuStore[],
  criteria: StoreCriteria
): FilteredStores => {
  const { currentPinnedStore, searchAddressCoords } = criteria;
  const storesWithDetails = getStoresWithDetails({
    stores,
    userLocation: searchAddressCoords,
  });

  const sortedStoresByDistance =
    searchAddressCoords?.lat && searchAddressCoords?.long
      ? nearbySort(
          {
            lat: searchAddressCoords.lat,
            long: searchAddressCoords.long,
          },
          storesWithDetails
        )
      : storesWithDetails;

  // Move pinned store to the front of the array
  if (currentPinnedStore) {
    sortedStoresByDistance.forEach((store, i) => {
      if (store.id === currentPinnedStore.id) {
        sortedStoresByDistance.splice(i, 1);
        sortedStoresByDistance.unshift(store);
      }
    });
  }

  return {
    availableStores: filteredStores(sortedStoresByDistance, criteria),
    currentStore: storesWithDetails.find(
      (store) => store.id === currentPinnedStore?.id
    ),
    deliveryStores: sortedStoresByDistance.filter(({ delivery }) => delivery),
    medStores: sortedStoresByDistance.filter(
      ({ recreational }) => !recreational
    ),
    // Curbside is a sub-mode of pickup — a curbside store always has pickup:true —
    // so the pickup tab already represents every pickup-or-curbside store.
    pickupStores: sortedStoresByDistance.filter(({ pickup }) => pickup),
    recStores: sortedStoresByDistance.filter(
      ({ recreational }) => recreational
    ),
  };
};

/**
 * Exported just for testing; prefer filteredAndSortedStores if possible.
 * @private
 */
export const filteredStores = (
  stores: BloomMenuStore[],
  { deliverableStoreIds, fulfillmentMode, reservationMode }: StoreCriteria
): BloomMenuStore[] => {
  return stores
    .filter(({ recreational }) => {
      return (
        fulfillmentMode === 'both' ||
        (fulfillmentMode === 'rec' && recreational) ||
        (fulfillmentMode === 'med' && !recreational)
      );
    })
    .filter(({ delivery, id, pickup }) => {
      return (
        (reservationMode === 'pickup' && pickup) ||
        (reservationMode === 'delivery' &&
          delivery &&
          (!deliverableStoreIds || deliverableStoreIds.includes(id.toString())))
      );
    });
};
